import React, { useState } from 'react';
import { AppLink } from '../routing';
import { useAuth } from '../lib/auth';
import { AdminMedia } from './AdminMedia';
import { AdminShows } from './AdminShows';

type AdminTab = 'shows' | 'media';

export function AdminPage() {
  const { user, loading: authLoading, login, logout } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tab, setTab] = useState<AdminTab>('shows');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not sign in.');
    } finally {
      setBusy(false);
    }
  };

  if (authLoading) {
    return (
      <section className="admin-shell">
        <p className="meta">Checking sign-in…</p>
      </section>
    );
  }

  if (!user) {
    return (
      <section className="admin-shell">
        <div className="admin-header">
          <div>
            <p className="eyebrow">Harlan</p>
            <h1>Admin</h1>
            <p className="lede">Sign in to manage shows and media.</p>
          </div>
        </div>
        <form className="contact-form admin-form" onSubmit={onLogin}>
          <label>
            Email
            <input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </label>
          <label>
            Password
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>
          <div className="form-actions">
            <button className="hero-btn" type="submit" disabled={busy}>
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
            <AppLink className="text-link" href="/">
              Back to site
            </AppLink>
          </div>
          {error && <p className="status error">{error}</p>}
        </form>
      </section>
    );
  }

  return (
    <section className="admin-shell">
      <div className="admin-header">
        <div>
          <p className="eyebrow">{tab === 'shows' ? 'Shows' : 'Media'}</p>
          <h1>Admin console</h1>
          <p className="meta">{user.email}</p>
        </div>
        <div className="admin-toolbar">
          <AppLink className="text-link" href={tab === 'shows' ? '/shows' : '/media'}>
            View public {tab}
          </AppLink>
          <button className="hero-btn" type="button" onClick={() => logout()}>
            Sign out
          </button>
        </div>
      </div>

      <div className="admin-tabs" role="tablist" aria-label="Admin sections">
        <button
          className={`admin-tab${tab === 'shows' ? ' is-active' : ''}`}
          type="button"
          role="tab"
          aria-selected={tab === 'shows'}
          onClick={() => setTab('shows')}
        >
          Shows
        </button>
        <button
          className={`admin-tab${tab === 'media' ? ' is-active' : ''}`}
          type="button"
          role="tab"
          aria-selected={tab === 'media'}
          onClick={() => setTab('media')}
        >
          Media
        </button>
      </div>

      {tab === 'shows' ? <AdminShows /> : <AdminMedia />}
    </section>
  );
}
